// Port "Infografik im 3D-Raum" — aus Jans Renderer uebernommen (1.0
// RendererWebGL.ts, updateInfographicMesh).
//
// Eine Infografik kann auf zwei Arten in der Szene liegen:
//   • als HTML-Ebene (Vorgabe): DOM-Overlay ueber der 3D-Szene, Aufbau wird im
//     SVG selbst animiert (Balken wachsen, Zahlen zaehlen hoch).
//   • als 3D-FLAECHE (Checkbox "Als HTML rendern" aus): das SVG wird EINMAL pro
//     Inhaltsaenderung zu einer Textur gerastert und auf eine Ebene gelegt. Erst
//     dadurch sortiert sie sich mit 3D-Objekten in die Tiefe ein (kann also
//     hinter einer Kugel liegen, durch Glas gesehen werden usw.). Der Aufbau
//     laeuft hier als Wischblende ueber einen Shader-Wert — deshalb aendert sich
//     das Animationsverhalten in diesem Modus, wie Jan es beschrieben hat.
//
// Die Rasterung selbst ist bewusst einmalig: ein Choropleth-SVG ist ~113 KB,
// das pro Bild neu zu zeichnen wuerde die Bildrate ruinieren.
import THREE from "../utils/THREE";
import { buildInfographicSvg, infografikMessSchriften, infografikSchnitte, infografikTitelSchrift, infographicPixelSize } from "./infographicSvg";
import { ladeWortwolkenSchriften } from "./wortwolkenSchriften";
import { STANDARD_FONTS, schriftDatei } from "../assets/fonts";
import type { InfographicContent } from "@scrolly/post-types";

const MAX_TEXTURE_PX = 4096;

// Ein <img> mit SVG-Quelle ist ein ABGESCHOTTETES Dokument: es sieht weder die
// @font-face-Regeln der Seite noch darf es Dateien nachladen. Ohne Gegenmassnahme
// faellt jede Beschriftung auf Times zurueck — im HTML-Modus stimmt die Schrift,
// im 3D-Modus nicht. Deshalb die Schriftdatei EINMAL holen und als data:-URI in
// das SVG einbetten.
const fontDataUriCache: { [url: string]: Promise<string | null> } = {};

function alsDataUri(url: string): Promise<string | null> {
    if (!fontDataUriCache[url]) {
        fontDataUriCache[url] = fetch(url)
            .then((r) => r.blob())
            .then(
                (b) =>
                    new Promise<string | null>((resolve) => {
                        const fr = new FileReader();
                        fr.onload = () => resolve(typeof fr.result === "string" ? fr.result : null);
                        fr.onerror = () => resolve(null);
                        fr.readAsDataURL(b);
                    })
            )
            .catch(() => null);
    }
    return fontDataUriCache[url];
}

function embeddedFontCss(family: string | undefined, schnitte?: number[]): Promise<string> {
    if (!family) return Promise.resolve("");
    // Mit gewähltem Schnitt (07.10.2026): je gezeichnete Stärke die echte
    // Schnitt-Datei einbetten — mit nur einer Datei zeichnete das abgeschottete
    // SVG „Light“ als Regular und Fettes künstlich verdickt. Ohne Wahl bleibt
    // es bei der einen Datei wie bisher.
    const dateien = (schnitte ?? [])
        .filter((g, i, alle) => alle.indexOf(g) === i)
        .map((g) => ({ g, url: schriftDatei(family, g) }))
        .filter((d): d is { g: number; url: string } => !!d.url);
    if (dateien.length > 0) {
        return Promise.all(
            dateien.map(({ g, url }) =>
                alsDataUri(url).then((dataUri) =>
                    dataUri ? `@font-face{font-family:'${family}';font-weight:${g};src:url(${dataUri});}` : ""
                )
            )
        ).then((regeln) => (regeln.join("") ? `<style>${regeln.join("")}</style>` : ""));
    }
    const url = (STANDARD_FONTS as Record<string, string>)[family];
    if (!url) return Promise.resolve("");
    return alsDataUri(url).then((dataUri) =>
        dataUri
            ? `<style>@font-face{font-family:'${family}';src:url(${dataUri});}</style>`
            : ""
    );
}

/** Wartezeit nach einem Fehlbau, bevor die Grafik erneut gebaut wird. */
export const INFOGRAFIK_RETRY_MS = 500;

/**
 * Selbstheilung nach einem Fehlbau (Jans Fund 09.09.2026: Chart im frisch
 * geladenen Welcome-Scrolly leer, nach Vorschau wieder da). Vorher speicherte
 * der HTML-Pfad trotz Fehler die Signatur und zeigte eine leere Karte, bis
 * sich der Inhalt aenderte; der 3D-Pfad blieb fuer immer auf "laedt".
 *
 * Hier: Wartezeit setzen (kein Bau-Sturm pro Bild) und den Fehler EINMAL je
 * Inhalt laut melden — mit Typ, Modus und Datenstand, damit die Ursache beim
 * naechsten Mal in der Konsole steht statt nur die Wirkung.
 */
export function meldeInfografikBauFehler(
    host: Pick<InfographicMeshHost, "infographicRetryAt" | "infographicFehlerSig">,
    layerId: string,
    content: InfographicContent,
    sig: string,
    modus: "HTML" | "3D",
    error: unknown
): void {
    if (!host.infographicRetryAt) host.infographicRetryAt = {};
    if (!host.infographicFehlerSig) host.infographicFehlerSig = {};
    host.infographicRetryAt[layerId] = performance.now() + INFOGRAFIK_RETRY_MS;
    if (host.infographicFehlerSig[layerId] === sig) return;
    host.infographicFehlerSig[layerId] = sig;
    const c = content as TODOAny;
    console.error(
        `[Infografik ${layerId}] Bau der Grafik fehlgeschlagen — naechster Versuch in ${INFOGRAFIK_RETRY_MS} ms. ` +
            `Typ: ${c?.chartType ?? "-"}, Modus: ${modus}, Datenpunkte: ${Array.isArray(c?.data) ? c.data.length : "-"}, ` +
            `Schrift: ${c?.fontFamily || "-"}`,
        error
    );
}

/** Darf jetzt (wieder) gebaut werden? Falsch, solange die Wartezeit nach einem Fehlbau laeuft. */
export function infografikBauErlaubt(
    host: Pick<InfographicMeshHost, "infographicRetryAt">,
    layerId: string
): boolean {
    const bis = host.infographicRetryAt?.[layerId];
    return !bis || performance.now() >= bis;
}

/** Vorgabe: als HTML rendern. Die Checkbox im Menue schaltet auf die 3D-Flaeche. */
export function infographicIsHtml(content: InfographicContent): boolean {
    return (content as TODOAny).asHtml ?? true;
}

export interface InfographicMeshHost {
    layerMeshes: { [key: string]: TODOAny };
    layerState3d: { [key: string]: TODOAny };
    layerContentCache: { [key: string]: TODOAny };
    layerIsLoading: { [key: string]: boolean };
    infographicSig: { [key: string]: string };
    /** Selbstheilung: nach einem Fehlbau fruehestens ab diesem Zeitpunkt neu versuchen. */
    infographicRetryAt?: { [key: string]: number };
    /** Selbstheilung: Signatur des zuletzt gemeldeten Fehlbaus (nur einmal je Inhalt melden). */
    infographicFehlerSig?: { [key: string]: string };
    webGLRenderer: THREE.WebGLRenderer | undefined;
    uploadToGpu: (object: TODOAny) => void;
    markLayerLoaded: (layerId: string, url: string) => void;
    props: TODOAny;
}

// Schlagschatten der HTML-Karte, 1:1 in SVG-Einheiten uebersetzt.
// ZWEISCHICHTIG wie der Standard-Schatten der HTML-Boxen (TextOverlayNormal:
// `0 8px 32px rgba(0,0,0,0.45), 0 2px 8px rgba(0,0,0,0.25)`): weicher
// Ambient-Schatten + knackiger Kontaktschatten — eine einzelne weiche Schicht
// wirkte daneben zu diffus (Jans Fund). Die CSS-Werte sind GERAETE-Pixel auf
// der fertig skalierten Karte; im SVG rechnen wir in Slide-Einheiten, also
// durch den Buehnen-Massstab teilen. Bei CSS entspricht der Weichzeichnungs-
// Radius zwei Standardabweichungen, deshalb die Haelfte fuer stdDeviation.
const SHADOW_LAYERS_CSS = [
    { dy: 8, blur: 32, opacity: 0.45 },
    { dy: 2, blur: 8, opacity: 0.25 },
] as const;

/**
 * EINSTELLBARER Karten-Schatten (Jan 14.10.2026, „der Schatten lässt sich
 * hier nicht wirklich verändern"): `bgShadowWeich` (0–120, wie die
 * Weichheit der Bild-Schatten) und `bgShadowStaerke` (0–1) skalieren die
 * beiden Schichten. Ohne Angabe gilt 50 / 0,5 — das ergibt EXAKT die
 * bisherigen festen Werte, Bestands-Scrollys sehen unverändert aus.
 * VERSATZ WÄCHST MIT (Jan 21.10.2026, „der Schlagschatten hat einen zu
 * großen x/y-Versatz"): auch `dy` skaliert mit der Weichheit — bei der
 * Scene-Weichheit 8 rund 1,3 px statt fest 8 px (sonst stand ein fast
 * scharfer Schatten als Doppelkontur darunter).
 */
export function infografikSchattenSchichten(
    content: unknown
): Array<{ dy: number; blur: number; opacity: number }> {
    const c = (content ?? {}) as { bgShadowWeich?: unknown; bgShadowStaerke?: unknown };
    const zahl = (v: unknown, std: number) =>
        typeof v === "number" && Number.isFinite(v) ? v : std;
    const weich = Math.max(0, zahl(c.bgShadowWeich, 50)) / 50;
    const staerke = Math.max(0, zahl(c.bgShadowStaerke, 0.5)) / 0.5;
    return SHADOW_LAYERS_CSS.map((l) => ({
        dy: l.dy * weich,
        blur: l.blur * weich,
        opacity: Math.min(1, l.opacity * staerke),
    }));
}

/** Dasselbe als CSS-`filter` für die HTML-Karte. */
export function infografikSchattenCss(content: unknown): string {
    return infografikSchattenSchichten(content)
        .map((l) => `drop-shadow(0 ${+l.dy.toFixed(2)}px ${+l.blur.toFixed(2)}px rgba(0,0,0,${+l.opacity.toFixed(3)}))`)
        .join(" ");
}

/**
 * Baut/aktualisiert die 3D-Flaeche einer Infografik. Der Aufbau ist asynchron —
 * das SVG dekodiert der Browser als Bild.
 *
 * `unitsToPx` ist der aktuelle Buehnen-Massstab (Layer-Einheit -> Pixel). Er
 * wird nur fuer den Schlagschatten gebraucht, damit dessen Groesse der
 * HTML-Variante entspricht.
 */
export function updateInfographicMesh(
    host: InfographicMeshHost,
    layerId: string,
    content: InfographicContent,
    unitsToPx = 1
): void {
    // Der Aufbau steckt hier NICHT in der Signatur: die Textur wird bei voller
    // Fuellung gerastert, die Wischblende laeuft ueber den Shader. Sonst wuerde
    // jede Scroll-Bewegung neu rastern.
    //
    // Der Massstab dagegen SCHON — aber grob gerundet: der Schlagschatten ist in
    // die Textur eingebacken, also muss ein Formatwechsel sie neu rastern. Ohne
    // Rundung taete er das bei jedem Pixel Groessenaenderung.
    const shadow = (content as TODOAny).bgShadow ? Math.round(unitsToPx * 200) : 0;
    const sig = `${JSON.stringify(content)}|webgl|${shadow}`;
    const haveMesh = !!host.layerMeshes[layerId];
    if (haveMesh && host.infographicSig[layerId] === sig) return;
    if (host.layerIsLoading[layerId]) return;
    if (!infografikBauErlaubt(host, layerId)) return;

    // fuer die Fehlermeldung unten, falls der Browser das SVG nicht verdaut
    let lastSvg = "";
    const base = infographicPixelSize(content);
    // Der Schatten ragt ueber die Karte hinaus. Das SVG beschneidet an seinem
    // Rand, also braucht die Flaeche ringsum Luft — sonst waere der Schatten
    // abgeschnitten. Die Karte selbst bleibt dabei exakt gleich gross, sie sitzt
    // nur mittig in einer etwas groesseren Flaeche.
    const shadowOn = !!(content as TODOAny).bgShadow;
    const scale = unitsToPx > 0 ? 1 / unitsToPx : 0;
    // Luft fuer die am weitesten ausladende Schicht (die weiche dominiert)
    const pad = shadowOn
        ? Math.ceil(
              Math.max(
                  ...infografikSchattenSchichten(content).map((l) => (l.dy + (l.blur / 2) * 3) * scale)
              )
          )
        : 0;
    const pxW = base.width + pad * 2;
    const pxH = base.height + pad * 2;
    host.layerIsLoading[layerId] = true;

    const img = new Image();
    img.onload = () => {
        // Doppelt rastern -> bleibt beim Heranfahren scharf. Gedeckelt, damit
        // eine lange Zeitleiste (waechst mit der Stationszahl) nicht ueber die
        // Texturgrenze der Grafikkarte laeuft und schwarz bliebe.
        const DPI = Math.min(2, MAX_TEXTURE_PX / Math.max(pxW, pxH, 1));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(pxW * DPI));
        canvas.height = Math.max(1, Math.round(pxH * DPI));
        const ctx = canvas.getContext("2d")!;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const texture = new THREE.CanvasTexture(canvas);
        texture.minFilter = THREE.LinearMipmapLinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.anisotropy = host.webGLRenderer?.capabilities?.getMaxAnisotropy?.() ?? 1;
        texture.needsUpdate = true;

        let mesh = host.layerMeshes[layerId] as THREE.Mesh | undefined;
        if (mesh) {
            // Mesh weiterverwenden: nur Textur tauschen, Flaeche bei Bedarf neu
            const mat = mesh.material as THREE.MeshBasicMaterial;
            mat.map?.dispose();
            mat.map = texture;
            mat.needsUpdate = true;
            if (mesh.userData.pxW !== pxW || mesh.userData.pxH !== pxH) {
                mesh.geometry.dispose();
                const g = new THREE.PlaneGeometry(1, 1);
                g.scale(pxW, pxH, 1);
                mesh.geometry = g;
            }
        } else {
            const material = new THREE.MeshBasicMaterial({
                map: texture,
                transparent: true,
                depthWrite: false,
                side: THREE.DoubleSide,
                alphaTest: 0.01,
                name: String(layerId),
                opacity: 0, // pro Bild in renderLayer gesetzt
            });
            // Wischblende von links nach rechts: alles rechts von uReveal wird
            // verworfen. Als Shader-Wert kostet das Animieren keine Rasterung.
            //
            // ACHTUNG three-Version: die Textur-Koordinate heisst hier vMapUv,
            // NICHT vUv wie in Jans 1.0 (three r139). Seit r152 hat jede Map ihr
            // eigenes Varying (vMapUv/vAlphaMapUv/…). Mit vUv scheiterte die
            // Shader-Uebersetzung STILL — die Flaeche blieb komplett unsichtbar,
            // ohne Fehlermeldung in der Konsole.
            //
            // uRevealStart ist der linke Rand der KARTE in der Textur. Mit
            // Schlagschatten ist die Flaeche ringsum groesser als die Karte —
            // ohne diesen Startwert liefe der erste Teil des Aufbaus im leeren
            // Schattenrand ab und man saehe erst spaeter etwas. Ohne Schatten
            // ist er 0 und die Blende verhaelt sich wie zuvor.
            //
            // RICHTUNG: normalerweise von links nach rechts. Eine SENKRECHTE
            // Zeitleiste laeuft aber von oben nach unten — dort muss die Blende
            // der Achse folgen, sonst passt sie nicht zum Ablauf (Jans Wunsch).
            // In der Textur zeigt v nach OBEN (Zeile 0 des Bildes liegt bei
            // v = 1), deshalb ist die senkrechte Blende gespiegelt.
            const uReveal = { value: 1 };
            const uRevealStart = { value: 0 };
            const uRevealVertical = { value: 0 };
            material.onBeforeCompile = (shader: TODOAny) => {
                shader.uniforms.uReveal = uReveal;
                shader.uniforms.uRevealStart = uRevealStart;
                shader.uniforms.uRevealVertical = uRevealVertical;
                shader.fragmentShader =
                    "uniform float uReveal;\nuniform float uRevealStart;\n" +
                    "uniform float uRevealVertical;\n" +
                    shader.fragmentShader.replace(
                        "#include <map_fragment>",
                        "#include <map_fragment>\n" +
                            "  float peEdge = mix( uRevealStart, 1.0, uReveal );\n" +
                            "  if ( uRevealVertical > 0.5 ) {\n" +
                            "    if ( vMapUv.y < 1.0 - peEdge ) discard;\n" +
                            "  } else {\n" +
                            "    if ( vMapUv.x > peEdge ) discard;\n" +
                            "  }"
                    );
            };
            material.userData.uReveal = uReveal;
            material.userData.uRevealStart = uRevealStart;
            material.userData.uRevealVertical = uRevealVertical;
            const geometry = new THREE.PlaneGeometry(1, 1);
            geometry.scale(pxW, pxH, 1);
            mesh = new THREE.Mesh(geometry, material);
            host.layerMeshes[layerId] = mesh;
        }
        mesh.userData.pxW = pxW;
        mesh.userData.pxH = pxH;
        // Senkrechte Zeitleiste -> Blende laeuft von oben nach unten, sonst von
        // links nach rechts. Der Startwert bezieht sich entsprechend auf die
        // andere Kante der Flaeche.
        const vertical =
            (content as TODOAny).chartType === "timeline" &&
            (content as TODOAny).timelineOrientation === "vertical";
        const vertU = (mesh.material as TODOAny).userData?.uRevealVertical;
        if (vertU) vertU.value = vertical ? 1 : 0;
        const startU = (mesh.material as TODOAny).userData?.uRevealStart;
        if (startU) {
            const span = vertical ? pxH : pxW;
            startU.value = span > 0 ? pad / span : 0;
        }
        // Eine voll deckende Karte darf im opaken Durchgang bleiben und ist
        // damit DURCH GLAS sichtbar (Lupe). Eine durchscheinende Karte bleibt
        // transparent — ein halbdurchsichtiges Panel laesst sich nicht sinnvoll
        // brechen. alphaNeeded === false heisst "kann opak".
        (mesh.material as TODOAny).userData.alphaNeeded =
            (((content as TODOAny).backgroundColor?.a ?? 1) as number) < 1;
        host.uploadToGpu?.(mesh);
        host.layerState3d[layerId] = mesh as TODOAny;
        host.layerContentCache[layerId] = content;
        host.infographicSig[layerId] = sig;
        host.layerIsLoading[layerId] = false;
        if (host.infographicRetryAt) delete host.infographicRetryAt[layerId];
        // Ladeanzeige quittieren — sonst wartet der Leser auf einen Layer, der
        // nie "fertig" meldet. Nur beim ERSTEN Mal (siehe markLayerLoaded): die
        // Textur wird bei jeder Inhaltsaenderung neu gerastert.
        host.markLayerLoaded?.(layerId, `infographic:${layerId}`);
    };
    img.onerror = () => {
        host.layerIsLoading[layerId] = false;
        host.markLayerLoaded?.(layerId, `infographic:error`);
        // LAUT scheitern. Vorher gab dieser Zweig stillschweigend auf: kein Mesh,
        // keine Meldung — die Infografik fehlte im 3D-Modus einfach, ohne jede
        // Spur zur Ursache. Der Browser verraet bei einem SVG-Bild nicht, WAS er
        // nicht verdauen konnte, also geben wir wenigstens den Bauplan mit aus.
        console.error(
            `[Infografik ${layerId}] Die Grafik liess sich nicht zu einer Textur ` +
                `rastern — im 3D-Modus bleibt die Ebene deshalb leer. ` +
                `Typ: ${(content as TODOAny).chartType}, Flaeche: ${pxW}x${pxH}, ` +
                `Schrift: ${(content as TODOAny).fontFamily || "-"}, ` +
                `Datenpunkte: ${(content as TODOAny).data?.length ?? "-"}`,
            { svgAnfang: lastSvg.slice(0, 400), svgLaenge: lastSvg.length }
        );
    };

    // Fehlbau darf die Ebene nicht auf "laedt" haengen lassen (sonst nie
    // wieder ein Versuch, bis der Renderer neu aufgebaut wird).
    const bauFehler = (error: unknown) => {
        host.layerIsLoading[layerId] = false;
        meldeInfografikBauFehler(host, layerId, content, sig, "3D", error);
    };
    // Wortwolke (17.10.2026): bis zu drei Schriften einbetten, und erst bauen,
    // wenn sie auch auf der Seite geladen sind — die Engine misst die Wörter.
    const schriftCss =
        content.chartType === "wordcloud" || content.chartType === "table" || content.chartType === "steps"
            ? ladeWortwolkenSchriften(content).then(() =>
                  Promise.all(
                      infografikMessSchriften(content)
                          .filter((s, i, alle) => alle.findIndex((x) => x.family === s.family && x.weight === s.weight) === i)
                          .map((s) => embeddedFontCss(s.family || undefined, [s.weight]))
                  ).then((teile) => teile.join(""))
              )
            : embeddedFontCss((content as TODOAny).fontFamily, infografikSchnitte(content));
    // eigene Titel-Schrift (Jan 18.10.2026) zusätzlich einbetten
    const titel = infografikTitelSchrift(content);
    const titelCss =
        content.title && content.chartType !== "kpi" && content.titleFont?.family
            ? embeddedFontCss(titel.family, [titel.weight])
            : Promise.resolve("");
    void Promise.all([schriftCss, titelCss]).then(([basisCss, extraCss]) => {
      const fontCss = basisCss + extraCss;
      try {
        const built = buildInfographicSvg(content, 1);
        // Schlagschatten einbacken: alles ausser dem oeffnenden/schliessenden
        // <svg>-Tag in eine Gruppe mit feDropShadow legen. Der Filter folgt —
        // wie CSS drop-shadow — der Silhouette des ganzen Inhalts, nicht nur dem
        // Karten-Rechteck. Die Gruppe wird um `pad` verschoben, damit die Karte
        // trotz der groesseren Flaeche mittig sitzt.
        const head = built.slice(0, built.indexOf(">") + 1);
        const inner = built.slice(head.length, built.lastIndexOf("</svg>"));
        const filterId = `pe-igshadow-${layerId}`;
        // Beide Schichten hintereinander — SVG-Filterprimitive verketten sich
        // automatisch, genau wie zwei drop-shadow()-Funktionen im DOM-Pfad.
        const defs = shadowOn
            ? `<defs><filter id="${filterId}" x="-25%" y="-25%" width="150%" height="150%">` +
              infografikSchattenSchichten(content).map(
                  (l) =>
                      `<feDropShadow dx="0" dy="${(l.dy * scale).toFixed(2)}" ` +
                      `stdDeviation="${((l.blur / 2) * scale).toFixed(2)}" ` +
                      `flood-color="black" flood-opacity="${l.opacity}"/>`
              ).join("") +
              `</filter></defs>`
            : "";
        const open = shadowOn ? `<g filter="url(#${filterId})" transform="translate(${pad} ${pad})">` : "";
        const close = shadowOn ? "</g>" : "";
        // buildInfographicSvg liefert width/height 100% (fuer den DOM-Fall). Ein
        // <img> hat keinen Container, der eine Groesse vorgibt — ohne feste
        // Pixelmasse rastert der Browser auf seine 300x150-Vorgabe.
        const newHead = head
            .replace('width="100%" height="100%"', `width="${pxW}" height="${pxH}"`)
            .replace(/viewBox="0 0 [\d.]+ [\d.]+"/, `viewBox="0 0 ${pxW} ${pxH}"`);
        const svg = `${newHead}${fontCss}${defs}${open}${inner}${close}</svg>`;
        lastSvg = svg;
        img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
      } catch (error) {
        bauFehler(error);
      }
    }, bauFehler);
}
